import { addCalendarDays, displayCalendarDate, fromPickerDate, localTodayCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';

import type { DocumentEnvelopePayload, TimelineEnvelope } from '../../types';

/** Documents retain their authored calendar date; other events keep their effective timestamp. */
export function envelopeSortDate(e: TimelineEnvelope): string {
  if (e.kind === 'document') {
    const payload = (e.payload ?? {}) as DocumentEnvelopePayload;
    const authoredDate = payload.authored_at ? parseCalendarDate(payload.authored_at) : null;
    if (authoredDate) return authoredDate;
  }
  return e.effective_date || '';
}

/** Bare dates keep their components; actual timestamps use the browser's local calendar. */
function timelineCalendarDate(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parseCalendarDate(value);
  return fromPickerDate(new Date(value));
}

/**
 * Generated documents (lab orders, form PDFs, markdown note exports) flow
 * through the same `kind=document` envelope as DocRefs but originate from
 * the legacy DocumentMetadata table. They have no enrichment and use a
 * different download path. Detect via `extracted_by` (the envelope's
 * `derived_via` provenance string).
 */
export function isGeneratedDoc(envelope: TimelineEnvelope): boolean {
  return envelope.extracted_by === 'generated_document';
}

export interface PeriodGroup<T> {
  key: string;
  label: string;
  items: T[];
}

/**
 * Bucket items into "This month" / "Last month" / year buckets, using a
 * caller-supplied `getDate` extractor so the same helper works for both
 * artifacts and batch envelopes.
 */
export function groupByPeriod<T>(
  items: T[],
  getDate: (item: T) => string,
  now: Date = new Date(),
): PeriodGroup<T>[] {
  const thisMonth: PeriodGroup<T> = {
    key: 'this-month',
    label: 'This month',
    items: [],
  };
  const lastMonth: PeriodGroup<T> = {
    key: 'last-month',
    label: 'Last month',
    items: [],
  };
  const yearBuckets = new Map<number, PeriodGroup<T>>();

  const today = localTodayCalendarDate(now);
  const currentMonth = today.slice(0, 7);
  const previousMonth = today.startsWith('0001-01-')
    ? ''
    : addCalendarDays(today, -Number(today.slice(8))).slice(0, 7);

  for (const item of items) {
    const iso = getDate(item);
    const date = timelineCalendarDate(iso);
    if (!date) {
      const bucket =
        yearBuckets.get(0) ?? { key: 'undated', label: 'Undated', items: [] };
      bucket.items.push(item);
      yearBuckets.set(0, bucket);
      continue;
    }
    const sameMonth = date.slice(0, 7) === currentMonth;
    const sameLastMonth = date.slice(0, 7) === previousMonth;

    if (sameMonth) {
      thisMonth.items.push(item);
    } else if (sameLastMonth) {
      lastMonth.items.push(item);
    } else {
      const y = Number(date.slice(0, 4));
      const bucket =
        yearBuckets.get(y) ?? { key: String(y), label: String(y), items: [] };
      bucket.items.push(item);
      yearBuckets.set(y, bucket);
    }
  }

  // Validate the calendar day first to reject impossible dates. Sort the original
  // value to preserve within-day ordering for timestamps; bare dates sort at UTC
  // midnight, matching the server's effective_date projection.
  const sortTime = (item: T) => {
    const value = getDate(item);
    return timelineCalendarDate(value) ? new Date(value).getTime() : 0;
  };
  const byDesc = (a: T, b: T) => sortTime(b) - sortTime(a);

  thisMonth.items.sort(byDesc);
  lastMonth.items.sort(byDesc);

  const out: PeriodGroup<T>[] = [];
  if (thisMonth.items.length) out.push(thisMonth);
  if (lastMonth.items.length) out.push(lastMonth);

  // Years descending; "undated" (key=0) goes last
  const years = [...yearBuckets.keys()].sort((a, b) => {
    if (a === 0) return 1;
    if (b === 0) return -1;
    return b - a;
  });
  for (const y of years) {
    const bucket = yearBuckets.get(y);
    if (!bucket) continue;
    bucket.items.sort(byDesc);
    out.push(bucket);
  }
  return out;
}

/** "Mar 12, 2026" / "Mar 12 → Apr 8, 2026" / "Mar 12, 2024 → Apr 8, 2026" */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '';
  const s = timelineCalendarDate(start);
  if (!s) return '';
  const e = end ? timelineCalendarDate(end) : null;
  const fullOpts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  if (!e || start === end) return displayCalendarDate(s, undefined, fullOpts);
  if (s.slice(0, 4) === e.slice(0, 4)) {
    const shortOpts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };
    return `${displayCalendarDate(s, undefined, shortOpts)} → ${displayCalendarDate(e, undefined, fullOpts)}`;
  }
  return `${displayCalendarDate(s, undefined, fullOpts)} → ${displayCalendarDate(e, undefined, fullOpts)}`;
}

/**
 * Card copy for in-progress DocRefs. The terminal states (`enriched` /
 * `failed`) render a populated card or error — `pending` shows a skeleton
 * with this caption.
 */
export function docRefStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'pending':
      return 'Reading document…';
    case 'failed':
      return 'Processing failed';
    default:
      return '';
  }
}

export const TERMINAL_DOCREF_STATUSES = new Set(['enriched', 'failed']);

/** Renders the pre-DocRef "we have an artifact but no clinical record yet"
 *  ghost cards keyed off a known source_artifact_id. */
export function uploadStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'uploaded':
      return 'Uploaded — extracting records…';
    case 'decomposed':
      return 'Records identified — enriching…';
    case 'parsing':
      return 'Parsing pages…';
    case 'parsed':
      return 'Pages parsed — extracting fields…';
    case 'failed':
      return 'Upload processing failed';
    default:
      return 'Processing…';
  }
}
